import { Box, styled, useTheme } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { ReactElement, useEffect, useState } from 'react';

import { ArticleCardSlider } from '../../components/card-slider/article-card-slider/article-card-slider';
import { HeaderBanner } from '../../components/header-banner/header-banner';
import { NewsSlider } from '../../components/card-slider/news-slider/news-slider';
import { ROUTES } from '../../utils/routes';
import { SponsorBanner } from './sponsor-banner/sponsor-banner';
import { SupplierOfferSlider } from '../../components/card-slider/supplier-offer-slider/supplier-offer-slider';
import { articleSpecialOffersSelector } from '../../store/articles-selectors';
import { fetchActiveHeaderBanners } from '../../store/header-banner-actions';
import { fetchArticleSpecialOffers } from '../../store/articles-actions';
import { fetchNewsSliderItems } from '../../store/news-actions';
import { fetchSponsorBanners } from '../../store/sponsor-banner-actions';
import { fetchSupplierSpecialOffers } from '../../store/supplier-actions';
import { headerBannerSelector } from '../../store/header-banner-selectors';
import { newsSliderItemSelector } from '../../store/news-selectors';
import { supplierSpecialOffersSelector } from '../../store/supplier-selectors';
import { useAppDispatch, useAppSelector } from '../../../store/store';

const Section = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4, 0),
}));

export const CoverPage = (): ReactElement => {
    const theme = useTheme();

    const dispatch = useAppDispatch();
    const { supplierOffers, total: totalOffers } = useAppSelector(supplierSpecialOffersSelector);
    const { articleOffers, total: totalArticles } = useAppSelector(articleSpecialOffersSelector);
    const { news, total: totalNews } = useAppSelector(newsSliderItemSelector);
    const [articlePage, setArticlePage] = useState(1);
    const [supplierOfferPage, setSupplierOfferPage] = useState(1);
    const [newsPage, setNewsPage] = useState(1);
    const { items: headerBannerItems } = useAppSelector(headerBannerSelector);

    useEffect(() => {
        dispatch(fetchActiveHeaderBanners());
        dispatch(fetchSponsorBanners());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchArticleSpecialOffers(new URLSearchParams({ page: articlePage.toString() })));
    }, [articlePage, dispatch]);

    useEffect(() => {
        dispatch(fetchSupplierSpecialOffers(new URLSearchParams({ page: supplierOfferPage.toString() })));
    }, [supplierOfferPage, dispatch]);

    useEffect(() => {
        dispatch(fetchNewsSliderItems(new URLSearchParams({ page: newsPage.toString() })));
    }, [newsPage, dispatch]);

    return (
        <Box sx={{ mb: 2 }}>
            {!!headerBannerItems.length && <HeaderBanner items={headerBannerItems} />}
            {!!articleOffers.length && (
                <Section>
                    <ArticleCardSlider
                        title={<FormattedMessage id="app.article.slider.title" />}
                        articles={articleOffers}
                        totalItems={totalArticles}
                        showAllUrl={{
                            pathname: ROUTES.searchPage,
                            search: new URLSearchParams({ [FilterParams.SpecialPrice]: 'true' }).toString(),
                        }}
                        onLoadMore={() => setArticlePage(page => page + 1)}
                    />
                </Section>
            )}
            {!!supplierOffers.length && (
                <Section sx={{ backgroundColor: theme.palette.grey['100'] }}>
                    <SupplierOfferSlider
                        offers={supplierOffers}
                        totalItems={totalOffers}
                        onLoadMore={() => setSupplierOfferPage(page => page + 1)}
                    />
                </Section>
            )}
            {!!news.length && (
                <Section>
                    <SponsorBanner />
                    <NewsSlider
                        news={news}
                        totalItems={totalNews}
                        showAllUrl={ROUTES.news.base}
                        onLoadMore={() => setNewsPage(page => page + 1)}
                    />
                </Section>
            )}
        </Box>
    );
};
